import { useEffect, useRef, useState } from 'react';
import {
  defaultFloodIssue,
  findFloodCapability,
  findFloodCase,
  findFloodEvidence,
  findFloodSource,
  floodCapabilities,
  floodCases,
  floodCaseNotice,
  floodEvidence,
  floodEvidenceCategoryOrder,
  floodEvidenceGroupNote,
  floodEvidenceRelation,
  floodIssues,
  floodIssuesForCapability,
  floodSafetyNotice,
  type FloodCapability,
  type FloodCase,
  type FloodEvidence,
  type FloodEvidenceCategory,
  type FloodIssue,
} from './flood-research-content';
import type { DetailLocation } from './ScenarioDetailView';
import { ScenarioCardArt } from './ScenarioCardArt';
import { FloodResearchImage } from './FloodResearchImage';
import { handleScenarioTabsKeyDown } from './scenario-tab-keyboard';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';
import { platformRoutes } from '../../../shared/platform/platform-routes';

/**
 * 洪水与城市内涝详情页（SC-FLOOD-01）。
 *
 * 与高层建筑火灾、森林火灾、危化品事故、交通与地下空间事故共用同一套阅读结构：
 * 面包屑、场景头、两个页签、左侧问题栏、右侧单一内容面、抽屉。本页不接入源绑定投影，
 * 也不伪造一份 ScenarioProjection：内容来自 `flood-research-content.ts` 的公开资料研究参考。
 *
 * 这一页只做四件事：说明问题、列出需要掌握的信息、说明哪些技术能力可以帮助获取信息、
 * 给出可追溯的官方依据与案例。它不生成实时水位或洪水预报、风险数值、警戒或疏散距离，
 * 不控制泵站、闸门，不指派救援，也不把某次灾害的地点说成适用范围内的通用结论。
 */
export function FloodScenarioDetailView({
  location,
  onLocation,
  onLibrary,
}: {
  readonly location: DetailLocation;
  readonly onLocation: (next: DetailLocation) => void;
  readonly onLibrary: () => void;
}) {
  const issue =
    floodIssues.find((row) => row.key === location.issue) ??
    floodIssues.find((row) => row.key === defaultFloodIssue) ??
    floodIssues[0];
  const [capability, setCapability] = useState<FloodCapability | null>(null);
  const [evidence, setEvidence] = useState<FloodEvidence | null>(null);
  const [all, setAll] = useState(false);
  const [category, setCategory] = useState<FloodEvidenceCategory | null>(null);
  const readingScroll = useRef(0);
  const previousTab = useRef(location.tab);
  const opener = useRef<HTMLElement | null>(null);
  const evidenceOpener = useRef<HTMLElement | null>(null);
  const information = useRef<HTMLElement>(null);
  const capabilities = useRef<HTMLElement>(null);
  useEffect(() => {
    setCapability(null);
    setEvidence(null);
    setAll(false);
    setCategory(null);
  }, [location.issue, location.tab]);
  useEffect(() => {
    if (location.tab !== 'issues' || !location.focus) return;
    (location.focus === 'information' ? information : capabilities).current?.scrollIntoView({
      block: 'start',
    });
  }, [location.issue, location.tab, location.focus]);
  useEffect(() => {
    if (previousTab.current === 'evidence' && location.tab === 'issues')
      window.scrollTo(0, readingScroll.current);
    if (previousTab.current === 'issues' && location.tab === 'evidence') window.scrollTo(0, 0);
    previousTab.current = location.tab;
  }, [location.tab]);
  if (!issue) return null;
  const changeTab = (tab: 'issues' | 'evidence') => {
    if (location.tab === 'issues' && tab === 'evidence') readingScroll.current = window.scrollY;
    onLocation({ ...location, tab, focus: null });
  };
  const related = issue.capabilityIds.flatMap((id) => {
    const row = findFloodCapability(id);
    return row ? [row] : [];
  });
  const evidenceRows = (all ? floodEvidence : issueEvidence(issue)).filter(
    (row) => category === null || row.category === category,
  );
  return (
    <section className="scc-detail scc-fl-detail" data-reference-nature="PUBLIC_RESEARCH_REFERENCE">
      <nav className="scc-breadcrumb" aria-label="面包屑">
        <button onClick={onLibrary}>场景中心</button>
        <span aria-hidden="true">›</span>
        <strong>洪水与城市内涝</strong>
      </nav>
      <header className="scc-scene-head">
        <div className="scc-scene-art">
          <ScenarioCardArt id="flood" name="洪水与城市内涝" />
        </div>
        <div className="scc-scene-title">
          <h1>洪水与城市内涝</h1>
          <p>暴雨雨情、河道洪水、地下空间倒灌、排水设施、人员安全与交通协同的公开资料研究参考。</p>
        </div>
        <button className="scc-scene-back" onClick={onLibrary}>
          返回场景库 →
        </button>
      </header>
      <div
        className="scc-tab-bar"
        role="tablist"
        aria-label="场景详情"
        onKeyDown={handleScenarioTabsKeyDown}
      >
        {(['issues', 'evidence'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            id={'scc-fl-tab-' + tab}
            aria-selected={location.tab === tab}
            aria-controls="scc-fl-panel"
            tabIndex={location.tab === tab ? 0 : -1}
            onClick={() => changeTab(tab)}
          >
            {tab === 'issues' ? '典型问题' : '依据与案例'}
          </button>
        ))}
      </div>
      <div id="scc-fl-panel" role="tabpanel" aria-labelledby={'scc-fl-tab-' + location.tab}>
        {location.tab === 'issues' ? (
          <div className="scc-issue-layout">
            <aside className="scc-issue-list" aria-label="典型问题">
              <h2>典型问题</h2>
              <ul>
                {floodIssues.map((row) => (
                  <li key={row.key}>
                    <button
                      data-issue={row.key}
                      aria-current={row.key === issue.key ? 'true' : undefined}
                      onClick={() => onLocation({ issue: row.key, tab: 'issues', focus: null })}
                    >
                      <span>{row.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p>选择问题，查看相关信息、能力与依据。</p>
            </aside>
            <div className="scc-issue-main">
              <article className="scc-issue-intro">
                <div>
                  <h2>{issue.title}</h2>
                  <p>{issue.summary}</p>
                  <p className="scc-issue-context">适用对象：{issue.context}</p>
                  <p className="scc-issue-impact">为什么需要关注：{issue.why}</p>
                  <ul className="scc-drawer-list scc-fl-questions">
                    {issue.questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </div>
                <FloodResearchImage file={issue.image} label={issue.title} />
              </article>
              <section className="scc-read-section" ref={information} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>需要掌握的信息</h2>
                  <button className="scc-text-button" onClick={() => changeTab('evidence')}>
                    查看本问题依据 ↗
                  </button>
                </div>
                <p className="scc-section-lead">
                  以下是需要了解的信息，未表示当前已经取得；缺失的部分按“待核实”保留。每一行的“查看依据”打开与该信息对应的官方章节或报告章节。
                </p>
                <ul className="scc-info-rows" data-linked-module="information">
                  {issue.information.map((row) => (
                    <li className="scc-info-row" key={row.name}>
                      <div className="scc-info-row-main">
                        <strong>{row.name}</strong>
                        <p>需要获取：{row.what}</p>
                        <p>通常来源：{row.acquisition}</p>
                      </div>
                      <button
                        className="scc-text-button scc-info-evidence"
                        onClick={(event) => {
                          evidenceOpener.current = event.currentTarget;
                          setEvidence(findFloodEvidence(row.evidenceId) ?? null);
                        }}
                      >
                        查看依据 ↗
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section" ref={capabilities} tabIndex={-1}>
                <h2>所需能力（研究主题）</h2>
                <p className="scc-section-lead">
                  本场景共整理 {floodCapabilities.length} 项能力研究主题，此处显示与当前问题相关的{' '}
                  {related.length}{' '}
                  项。这些主题只说明可以怎样获取或核验信息，不代表本地已配备、已接入或获准使用。
                </p>
                <ul className="scc-cap-grid" data-linked-module="capability">
                  {related.map((row) => (
                    <li
                      className="scc-cap-card scc-fl-card"
                      key={row.id}
                      data-research-capability={row.id}
                    >
                      <div className="scc-cap-art scc-fl-art">
                        <FloodResearchImage file={row.image} label={row.title} />
                      </div>
                      <div className="scc-cap-body">
                        <strong>{row.title}</strong>
                        <span className="scc-cap-scope" data-relation-marker>
                          研究关联 · 待专业审核
                        </span>
                        <p className="scc-cap-purpose">{row.role}</p>
                        <p className="scc-cap-output">可提供：{row.outputs.join('、')}</p>
                        <p className="scc-cap-output">限制：{row.limitation}</p>
                        <button
                          className="scc-text-button"
                          onClick={(event) => {
                            opener.current = event.currentTarget;
                            setCapability(row);
                          }}
                        >
                          阅读能力说明 →
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section">
                <h2>本问题的官方依据</h2>
                <ul
                  className="scc-tr-evidence-brief scc-fl-evidence-brief"
                  data-evidence-brief
                  data-issue={issue.id}
                >
                  {issueEvidence(issue).map((row) => (
                    <li key={row.id}>
                      <button
                        className="scc-text-button"
                        onClick={(event) => {
                          evidenceOpener.current = event.currentTarget;
                          setEvidence(row);
                        }}
                      >
                        {row.id} · {row.label}
                      </button>
                      <span className="scc-tr-evidence-loc">
                        {findFloodSource(row.sourceId)?.title}，{row.locator}
                      </span>
                    </li>
                  ))}
                </ul>
                <FloodCaseList ids={issue.caseIds} />
              </section>
              <footer className="scc-issue-footer">
                <span>所需能力仅为研究方向，尚无正式 Capability ID 映射。</span>
                <button className="scc-primary" onClick={() => changeTab('evidence')}>
                  查看依据与案例
                </button>
              </footer>
            </div>
          </div>
        ) : (
          <section className="scc-evidence-page">
            <div className="scc-heading-row">
              <h2>依据与案例</h2>
              <div className="scc-evidence-switch" role="group" aria-label="资料范围">
                <button aria-pressed={!all} onClick={() => setAll(false)}>
                  与本问题相关
                </button>
                <button aria-pressed={all} onClick={() => setAll(true)}>
                  查看本场景全部依据
                </button>
              </div>
            </div>
            <p className="scc-section-lead">
              {all
                ? `本场景共 ${floodEvidence.length} 张依据卡，按来源性质分类。`
                : `与“${issue.title}”相关的 ${issueEvidence(issue).length} 张依据卡。`}
              每张卡片给出文件、准确章节，以及不能据此推导的结论。
            </p>
            <div className="scc-evidence-switch" role="group" aria-label="资料类型">
              {([null, ...floodEvidenceCategoryOrder] as const).map((value) => (
                <button
                  key={value ?? 'all'}
                  aria-pressed={category === value}
                  onClick={() => setCategory(value)}
                >
                  {value ?? '全部类型'}
                </button>
              ))}
            </div>
            <FloodEvidenceGroups rows={evidenceRows} />
            {(!category || category === '灾害调查报告') && (
              <FloodCaseList ids={floodCases.map((row) => row.id)} grouped />
            )}
            <button className="scc-text-button" onClick={() => changeTab('issues')}>
              返回当前问题：{issue.title} ↑
            </button>
          </section>
        )}
      </div>
      <p className="scc-notice scc-chem-safety">{floodSafetyNotice}</p>
      {capability && (
        <FloodCapabilityDrawer
          capability={capability}
          issue={issue}
          restoreFocusTo={opener.current}
          onClose={() => setCapability(null)}
        />
      )}
      {evidence && (
        <FloodEvidenceDrawer
          evidence={evidence}
          issue={issue}
          restoreFocusTo={evidenceOpener.current}
          onClose={() => setEvidence(null)}
        />
      )}
    </section>
  );
}

function issueEvidence(issue: FloodIssue): readonly FloodEvidence[] {
  return issue.evidenceIds.flatMap((id) => {
    const row = findFloodEvidence(id);
    return row ? [row] : [];
  });
}

function evidenceIssues(id: string): readonly string[] {
  return floodIssues.filter((issue) => issue.evidenceIds.includes(id)).map((issue) => issue.title);
}

export function FloodEvidenceGroups({ rows }: { readonly rows: readonly FloodEvidence[] }) {
  if (rows.length === 0)
    return (
      <p className="scc-chem-empty">此范围暂无该类型的依据，可切换资料类型或查看本场景全部依据。</p>
    );
  return (
    <div data-evidence-kind="evidence">
      {floodEvidenceCategoryOrder.map((name) => {
        const grouped = rows.filter((row) => row.category === name);
        if (!grouped.length) return null;
        return (
          <section className="scc-evidence-group" data-evidence-group={name} key={name}>
            <h3>{name}</h3>
            <p className="scc-evidence-group-note">{floodEvidenceGroupNote[name]}</p>
            <div className="scc-evidence-grid">
              {grouped.map((row) => {
                const source = findFloodSource(row.sourceId);
                return (
                  <article className="scc-evidence-card" data-evidence-id={row.id} key={row.id}>
                    <span className="scc-chip">
                      {row.id} · {source?.org} · {source?.code}
                    </span>
                    <h3>{row.label}</h3>
                    <p>{row.plain}</p>
                    <p className="scc-evidence-locator">
                      准确位置：{source?.title}，{row.locator}
                    </p>
                    <p className="scc-evidence-use">不能据此推导：{row.cannot}</p>
                    <p className="scc-evidence-locator">
                      关联问题：{evidenceIssues(row.id).join('、')}
                    </p>
                    {source && (
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        打开官方原文 ↗
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function FloodCaseList({
  ids,
  grouped = false,
}: {
  readonly ids: readonly string[];
  readonly grouped?: boolean;
}) {
  const rows = [...new Set(ids)].flatMap((id) => {
    const row = findFloodCase(id);
    return row ? [row] : [];
  });
  if (rows.length === 0) return null;
  return (
    <div
      className="scc-chem-cases"
      data-evidence-kind="case"
      aria-label="郑州“7·20”调查报告相关事件"
    >
      <p className="scc-fl-case-notice" data-case-provenance="SAME_INVESTIGATION_REPORT">
        {floodCaseNotice}
      </p>
      <div className="scc-evidence-grid">
        {rows.map((row) => (
          <FloodCaseCard item={row} key={row.id} showSection={grouped} />
        ))}
      </div>
    </div>
  );
}

function FloodCaseCard({
  item,
  showSection,
}: {
  readonly item: FloodCase;
  readonly showSection: boolean;
}) {
  return (
    <article className="scc-evidence-card" data-case-id={item.id}>
      <span className="scc-chip">
        {item.kind} · {item.date} · {item.location}
      </span>
      <h3>{item.title}</h3>
      <p className="scc-tr-scope scc-fl-scope" data-scope-label={item.scopeLabel}>
        适用性质：{item.scopeLabel}
      </p>
      <p>{item.factual}</p>
      {showSection && <p className="scc-evidence-locator">对应章节：{item.section}</p>}
      <p className="scc-evidence-locator">与本研究的关系：{item.relevance}</p>
      <p className="scc-evidence-use">{item.scopeNote}</p>
      <p className="scc-evidence-use">不能据此推导：{item.limit}</p>
      {item.sourceIds.map((id) => {
        const source = findFloodSource(id);
        return source ? (
          <a key={id} href={source.url} target="_blank" rel="noopener noreferrer">
            打开官方原文：{source.title} ↗
          </a>
        ) : null;
      })}
    </article>
  );
}

/**
 * 依据抽屉：本任务最重要的部分。
 *
 * 六段固定顺序：这条依据说明什么 / 文件与准确位置 / 为什么与当前问题有关 /
 * 真实案例怎样印证（有关系才显示）/ 不能从中推导什么 / 官方原文或 PDF。
 * 读者不需要认识 sourceId、版本字段或 JSON，就能知道规则是什么、为什么相关、去哪里核查。
 */
export function FloodEvidenceDrawer({
  evidence,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly evidence: FloodEvidence;
  readonly issue: FloodIssue;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo ?? null);
  const source = findFloodSource(evidence.sourceId);
  const cases = evidence.caseIds.flatMap((id) => {
    const row = findFloodCase(id);
    return row ? [row] : [];
  });
  const primaryIssue = floodIssues.find((row) => row.id === evidence.issueIds[0]);
  const relationIssue = evidence.issueIds.includes(issue.id) ? issue : primaryIssue;
  return (
    <dialog
      className="scc-drawer scc-information-drawer scc-fl-evidence-drawer"
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scc-fl-evidence-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">
            洪水与城市内涝 · {issue.title} · 依据 {evidence.id}
          </p>
          <h2 id="scc-fl-evidence-title">{evidence.label}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <section className="scc-drawer-section">
          <h3>这条依据说明什么</h3>
          <p>{evidence.plain}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>文件与准确位置</h3>
          <ul className="scc-drawer-list">
            <li>文件名称：{source?.title ?? '来源未登记'}</li>
            <li>发布机关：{source?.org ?? '来源未登记'}</li>
            <li>文号：{source?.code ?? '来源未登记'}</li>
            <li>
              准确位置：{evidence.locator}
              {source ? `（${source.kind}，${source.published} 发布）` : ''}
            </li>
            {source && <li>来源性质：{source.kind}</li>}
            {source && <li>版本状态：{source.status}</li>}
          </ul>
        </section>
        <section className="scc-drawer-section">
          <h3>为什么与当前问题有关</h3>
          <p>{floodEvidenceRelation(relationIssue?.id ?? issue.id, evidence.id)}</p>
          <p className="scc-drawer-note">当前问题：{issue.title}</p>
          {relationIssue && relationIssue.id !== issue.id && (
            <p className="scc-drawer-note">此依据登记在：{relationIssue.title}</p>
          )}
        </section>
        {cases.length > 0 && (
          <section className="scc-drawer-section">
            <h3>真实案例怎样印证</h3>
            <p className="scc-fl-case-notice" data-case-provenance="SAME_INVESTIGATION_REPORT">
              {floodCaseNotice}
            </p>
            {cases.map((row) => (
              <article
                className="scc-tr-case-note scc-fl-case-note"
                data-drawer-case={row.id}
                key={row.id}
              >
                <strong>{row.title}</strong>
                <p className="scc-tr-scope scc-fl-scope">适用性质：{row.scopeLabel}</p>
                <p>{row.factual}</p>
                <p className="scc-evidence-locator">对应章节：{row.section}</p>
                <p className="scc-evidence-use">{row.scopeNote}</p>
              </article>
            ))}
          </section>
        )}
        <section className="scc-drawer-section">
          <h3>不能从中推导什么</h3>
          <p>{evidence.cannot}</p>
          {source && <p className="scc-drawer-note">适用范围：{source.scope}</p>}
        </section>
        <section className="scc-drawer-section">
          <h3>查看官方原文或 PDF</h3>
          {source ? (
            <>
              <a href={source.url} target="_blank" rel="noopener noreferrer">
                阅读官方原文 ↗
              </a>
              {source.documentUrl && (
                <p>
                  <a href={source.documentUrl} target="_blank" rel="noopener noreferrer">
                    {source.documentLabel ?? '官方 PDF'} ↗
                  </a>
                </p>
              )}
            </>
          ) : (
            <p className="scc-drawer-note">来源登记缺失，不提供外链。</p>
          )}
        </section>
        <p className="scc-drawer-note">
          官方原文外链；目标网络与 PDF 可达性未验证。本页未随包收录官方 PDF 原件。
        </p>
      </div>
    </dialog>
  );
}

export function FloodCapabilityDrawer({
  capability,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly capability: FloodCapability;
  readonly issue: FloodIssue;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo ?? null);
  const relatedIssues = floodIssuesForCapability(capability.id);
  return (
    <dialog
      className="scc-drawer scc-capability-drawer scc-chem-drawer"
      ref={dialog}
      aria-modal="true"
      aria-labelledby="scc-fl-capability-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">研究说明 · 洪水与城市内涝 · {issue.title}</p>
          <h2 id="scc-fl-capability-title">{capability.title}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <div className="scc-fl-drawer-hero">
          <FloodResearchImage file={capability.image} label={capability.title} />
        </div>
        <p className="scc-notice" data-capability-status="RESEARCH_TOPIC_ONLY">
          这是一项能力研究主题说明，不是正式 Capability 定义，也不表示设备已配备、已验证或已接入。
        </p>
        <section className="scc-drawer-section">
          <h3>它在获取信息中起什么作用</h3>
          <p>{capability.role}</p>
        </section>
        <div className="scc-chem-drawer-columns">
          <section className="scc-drawer-section">
            <h3>需要什么输入</h3>
            <ul className="scc-drawer-list">
              {capability.inputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
          <section className="scc-drawer-section">
            <h3>能提供什么输出</h3>
            <ul className="scc-drawer-list">
              {capability.outputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
        </div>
        <section className="scc-drawer-section">
          <h3>适用条件与限制</h3>
          <p>{capability.limitation}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>关联的典型问题</h3>
          <ul className="scc-drawer-list">
            {relatedIssues.map((title) => (
              <li key={title}>
                {title}
                {title === issue.title && <span className="scc-drawer-note"> · 当前问题</span>}
              </li>
            ))}
          </ul>
        </section>
        <p className="scc-drawer-footnote" data-definition-status="NO_APPROVED_MAPPING">
          能力定义暂不可查看：该研究主题尚无专业 Owner 批准的正式能力 ID 映射。
        </p>
        <a href={platformRoutes.equipment}>进入能力中心浏览／查找 →</a>
        <p className="scc-drawer-footnote">通用浏览入口，尚未精确匹配能力定义。</p>
      </div>
    </dialog>
  );
}
